<script setup lang="ts">
  import {
    OrganisationsService,
    EmailsService,
    type CustomerOrganisation,
    UsersService,
    type CustomerUser as User,
    type EmailRenderTemplateResponse
  } from '@/types/generated'
  import { onMounted, ref, computed, watch } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    EDITOR_COLORS,
    EDITOR_TEXT_SIZES,
    EDITOR_FONT_FAMILIES
  } from '@/shared/constants/app/editor/editor'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import EmailPreview from '@/components/app/email/EmailPreview.vue'
  import { GENERIC_EMAIL } from '@/constants/settings/email'

  const org = ref<CustomerOrganisation>()
  const user = ref<User>()
  const rawEmailTemplateHTML = computed(() => {
    return GENERIC_EMAIL + user.value?.email_signature + org.value?.email_signature
  })
  const template = ref<EmailRenderTemplateResponse>()

  const textFontOptions = computed(() => {
    return Object.entries(EDITOR_FONT_FAMILIES).map(([key, title]) => ({
      value: key,
      title: title
    }))
  })

  const textColorOptions = computed(() => {
    return Object.values(EDITOR_COLORS).map((value) => {
      return {
        value: value,
        title: value
      }
    })
  })

  const textSizeOptions = computed(() => {
    return Object.values(EDITOR_TEXT_SIZES).map((value) => {
      return {
        value: value,
        title: `${value}`
      }
    })
  })

  onMounted(async () => {
    org.value = (await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })) as CustomerOrganisation
    user.value = (await UsersService.usersRetrieve({
      uuid: 'current'
    })) as User
    template.value = await EmailsService.emailsRenderTemplateCreate({
      requestBody: {
        template_html: rawEmailTemplateHTML.value,
        subject: `Ihre Anfrage bei ${org.value.name}`
      }
    })
  })
  watch(
    org,
    async () => {
      await OrganisationsService.organisationsPartialUpdate({
        uuid: org.value.uuid,
        requestBody: {
          email_text_font: org.value.email_text_font,
          email_text_color: org.value.email_text_color,
          email_text_size: org.value.email_text_size
        }
      })
      template.value = await EmailsService.emailsRenderTemplateCreate({
        requestBody: {
          template_html: rawEmailTemplateHTML.value,
          subject: `Ihre Anfrage bei ${org.value.name}`
        }
      })
    },
    { deep: true }
  )
</script>
<template>
  <SettingsPage>
    <div v-if="org && user">
      <div class="mb-6 flex gap-4">
        <!-- 60% width of the parent -->
        <!-- Schriftart -->
        <div class="w-32">
          <div class="w-full">
            <!-- Adjust width as needed, e.g., w-32 for roughly 32 units of the available width -->
            <SelectField
              label="Schriftart"
              v-model="org.email_text_font"
              :options="textFontOptions"
              :is-filterable="false"
              name="email_text_font"
            >
              <template #button-contents="{ selectedOption }">
                <span
                  class="truncate text-gray-800"
                  :style="`font-family: ${selectedOption.value}`"
                >
                  {{ selectedOption === null ? selectedOption : selectedOption.title }}
                </span>
              </template>
              <template #option-contents="{ option }">
                <span
                  class="text-gray-800"
                  :style="`font-family: ${option.value}`"
                >
                  {{ option.title }}
                </span>
              </template>
            </SelectField>
          </div>
        </div>

        <!-- Schriftfarbe -->
        <div class="">
          <!-- Adjust width -->
          <SelectField
            label="Schriftfarbe"
            v-model="org.email_text_color"
            :options="textColorOptions"
            :is-filterable="false"
            name="email_text_color"
          >
            <template #button-contents="{ selectedOption }">
              <div
                class="me-2 inline-block h-4 w-4"
                :style="`background-color: ${selectedOption.value};`"
              ></div>
              <span class="truncate text-gray-800">
                {{ selectedOption === null ? selectedOption : selectedOption.title }}
              </span>
            </template>
            <template #option-contents="{ option }">
              <div
                class="me-2 inline-block h-4 w-4"
                :style="`background-color: ${option.value};`"
              ></div>
              <span class="truncate text-gray-800">
                {{ option.title }}
              </span>
            </template>
          </SelectField>
        </div>

        <!-- Schriftgröße -->
        <div class="w-32">
          <div class="w-full">
            <!-- Adjust width -->
            <SelectField
              :options="textSizeOptions"
              name="email_text_size"
              label="Schriftgröße"
              v-model="org.email_text_size"
            />
          </div>
        </div>
      </div>

      <div class="mb-3 block font-medium">Vorschau</div>
      <EmailPreview
        v-if="template"
        :subject="template.subject"
        recipient-email="max.mustermann@outlook.com"
        :from-email="org.email"
        :text-html="template.html"
      />
    </div>
  </SettingsPage>
</template>
